/**
 * Asset Management API
 *
 * Base path : /api/hr/assets
 * Auth      : Bearer token via shared api-client interceptor
 * Pagination: Spring Page convention — page 0-indexed, backend field "number" normalised to "page"
 */

import { api } from "#/lib/api-client";
import type {
  Asset, AssetAssignment,
  AssetByStatusReport, AssetCategory, AssetCreateRequest, AssetDashboardStats,
  AssetDisposal, AssetLocation, AssetMaintenance, AssetSummary, AssetUpdateRequest,
  AssignmentCreateRequest, DisposalApproveRequest, DisposalCompleteRequest,
  DisposalCreateRequest, ImportResultRow, MarkIncidentRequest,
  MaintenanceCreateRequest, MaintenanceUpdateRequest, PageResponse, ReturnAssetRequest,
} from "./types";

/** Build a query string, omitting undefined / null / empty-string values */
function q(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  return qs.toString();
}

/** Normalise Spring Page response — handles Spring Boot 3.3+ nested page metadata and envelope wrapping */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normPage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    page:          meta?.number        ?? meta?.page ?? 0,
    size:          meta?.size          ?? 0,
  };
}

// ─── Param types ──────────────────────────────────────────────────────────────

export type AssetListParams = {
  search?:     string;
  categoryId?: number;
  status?:     string;
  page?:       number;
  size?:       number;
};

export type AssignmentListParams = {
  activeOnly?: boolean;
  page?:       number;
  size?:       number;
};

export type DisposalListParams = {
  status?:  string;
  page?:    number;
  size?:    number;
};

export type MaintenanceListParams = {
  status?:  string;
  assetId?: number;
  page?:    number;
  size?:    number;
};

// ─── API ──────────────────────────────────────────────────────────────────────

export const assetApi = {

  // ── Dashboard / report summary ─────────────────────────────────────────────

  /** GET /api/hr/assets/reports/summary — ASSET_REPORT_VIEW */
  async dashboardStats(): Promise<AssetDashboardStats> {
    return (await api.get<AssetDashboardStats>("/api/hr/assets/reports/summary")).data;
  },

  /** GET /api/hr/assets/reports/by-status — ASSET_REPORT_VIEW */
  async statsByStatus(): Promise<AssetByStatusReport[]> {
    return (await api.get<AssetByStatusReport[]>("/api/hr/assets/reports/by-status")).data;
  },

  // ── Assets ────────────────────────────────────────────────────────────────

  /**
   * GET /api/hr/assets?search&categoryId&status&page&size
   * ASSET_VIEW — returns Page<AssetSummary>
   */
  async listAssets(page = 0, size = 10, params?: AssetListParams): Promise<PageResponse<AssetSummary>> {
    const qs = q({ page, size, ...params });
    const res = await api.get(`/api/hr/assets?${qs}`);
    return normPage(res.data);
  },

  /**
   * GET /api/hr/assets/my-assets
   * Any authenticated user — returns Page<AssetSummary>
   */
  async myAssets(page = 0, size = 50): Promise<PageResponse<AssetSummary>> {
    const res = await api.get(`/api/hr/assets/my-assets?${q({ page, size })}`);
    return normPage(res.data);
  },

  /** GET /api/hr/assets/{id} — ASSET_VIEW */
  async getAsset(id: number): Promise<Asset> {
    return (await api.get<Asset>(`/api/hr/assets/${id}`)).data;
  },

  /** POST /api/hr/assets — ASSET_CREATE */
  async createAsset(payload: AssetCreateRequest): Promise<Asset> {
    return (await api.post<Asset>("/api/hr/assets", payload)).data;
  },

  /** PUT /api/hr/assets/{id} — ASSET_EDIT */
  async updateAsset(id: number, payload: AssetUpdateRequest): Promise<Asset> {
    return (await api.put<Asset>(`/api/hr/assets/${id}`, payload)).data;
  },

  /** PATCH /api/hr/assets/{id}/flag-disposal?flagged=true|false — ASSET_DISPOSE */
  async flagForDisposal(id: number, flagged = true): Promise<void> {
    await api.patch(`/api/hr/assets/${id}/flag-disposal?flagged=${flagged}`);
  },

  /**
   * PATCH /api/hr/assets/{id}/mark-lost — ASSET_DISPOSE | ASSET_MANAGE
   * Sets status to LOST, clears assignee, records incident details.
   * Guard: rejects if asset is already DISPOSED or LOST.
   */
  async markAsLost(id: number, payload: MarkIncidentRequest = {}): Promise<Asset> {
    return (await api.patch<Asset>(`/api/hr/assets/${id}/mark-lost`, payload)).data;
  },

  /**
   * PATCH /api/hr/assets/{id}/mark-damaged — ASSET_DISPOSE | ASSET_MANAGE
   * Sets status to DAMAGED, leaves assignment intact, records incident details.
   */
  async markAsDamaged(id: number, payload: MarkIncidentRequest = {}): Promise<Asset> {
    return (await api.patch<Asset>(`/api/hr/assets/${id}/mark-damaged`, payload)).data;
  },

  // ── Assignments ───────────────────────────────────────────────────────────

  /**
   * POST /api/hr/assets/{id}/assign — ASSET_ASSIGN
   * Body: AssignmentCreateRequest
   */
  async assignAsset(assetId: number, payload: AssignmentCreateRequest): Promise<AssetAssignment> {
    return (await api.post<AssetAssignment>(`/api/hr/assets/${assetId}/assign`, payload)).data;
  },

  /**
   * POST /api/hr/assets/{id}/return — ASSET_ASSIGN
   * Body: ReturnAssetRequest
   */
  async returnAsset(assetId: number, payload: ReturnAssetRequest): Promise<AssetAssignment> {
    return (await api.post<AssetAssignment>(`/api/hr/assets/${assetId}/return`, payload)).data;
  },

  /**
   * GET /api/hr/assets/{assetId}/assignments?activeOnly&page&size
   * ASSET_VIEW — returns Page<AssetAssignment>
   */
  async assetAssignmentHistory(
    assetId: number,
    page = 0,
    size = 20,
    params?: AssignmentListParams,
  ): Promise<PageResponse<AssetAssignment>> {
    const qs = q({ page, size, ...params });
    const res = await api.get(`/api/hr/assets/${assetId}/assignments?${qs}`);
    return normPage(res.data);
  },

  /**
   * GET /api/hr/assets/employee/{employeeId}/assignments?activeOnly&page&size
   * ASSET_VIEW — returns Page<AssetAssignment>
   */
  async employeeAssignmentHistory(
    employeeId: number,
    page = 0,
    size = 20,
    params?: AssignmentListParams,
  ): Promise<PageResponse<AssetAssignment>> {
    const qs = q({ page, size, ...params });
    const res = await api.get(`/api/hr/assets/employee/${employeeId}/assignments?${qs}`);
    return normPage(res.data);
  },

  // ── Categories ────────────────────────────────────────────────────────────

  /** GET /api/hr/assets/categories/all — flat list for dropdowns */
  async listCategories(): Promise<AssetCategory[]> {
    return (await api.get<AssetCategory[]>("/api/hr/assets/categories/all")).data;
  },

  /** GET /api/hr/assets/categories?page&size — paginated */
  async listCategoriesPaged(page = 0, size = 50): Promise<PageResponse<AssetCategory>> {
    const res = await api.get(`/api/hr/assets/categories?${q({ page, size })}`);
    return normPage(res.data);
  },

  /** GET /api/hr/assets/categories/{id} */
  async getCategory(id: number): Promise<AssetCategory> {
    return (await api.get<AssetCategory>(`/api/hr/assets/categories/${id}`)).data;
  },

  /** POST /api/hr/assets/categories — ASSET_MANAGE */
  async createCategory(
    payload: Pick<AssetCategory, "code" | "name" | "description" | "depreciationRate" | "usefulLifeYears">,
  ): Promise<AssetCategory> {
    return (await api.post<AssetCategory>("/api/hr/assets/categories", payload)).data;
  },

  /** PUT /api/hr/assets/categories/{id} — ASSET_MANAGE */
  async updateCategory(id: number, payload: Partial<AssetCategory>): Promise<AssetCategory> {
    return (await api.put<AssetCategory>(`/api/hr/assets/categories/${id}`, payload)).data;
  },

  // ── Locations ─────────────────────────────────────────────────────────────

  /** GET /api/hr/assets/locations/all — flat list for dropdowns */
  async listLocations(): Promise<AssetLocation[]> {
    return (await api.get<AssetLocation[]>("/api/hr/assets/locations/all")).data;
  },

  /** GET /api/hr/assets/locations?page&size — paginated */
  async listLocationsPaged(page = 0, size = 50): Promise<PageResponse<AssetLocation>> {
    const res = await api.get(`/api/hr/assets/locations?${q({ page, size })}`);
    return normPage(res.data);
  },

  /** GET /api/hr/assets/locations/{id} */
  async getLocation(id: number): Promise<AssetLocation> {
    return (await api.get<AssetLocation>(`/api/hr/assets/locations/${id}`)).data;
  },

  /** POST /api/hr/assets/locations — ASSET_MANAGE */
  async createLocation(
    payload: Pick<AssetLocation, "code" | "name" | "building" | "floor" | "room" | "description">,
  ): Promise<AssetLocation> {
    return (await api.post<AssetLocation>("/api/hr/assets/locations", payload)).data;
  },

  /** PUT /api/hr/assets/locations/{id} — ASSET_MANAGE */
  async updateLocation(id: number, payload: Partial<AssetLocation>): Promise<AssetLocation> {
    return (await api.put<AssetLocation>(`/api/hr/assets/locations/${id}`, payload)).data;
  },

  // ── Maintenance ───────────────────────────────────────────────────────────

  /** GET /api/hr/assets/maintenance?assetId&status&page&size — ASSET_MAINTENANCE */
  async listMaintenance(
    page = 0,
    size = 10,
    params?: MaintenanceListParams,
  ): Promise<PageResponse<AssetMaintenance>> {
    const qs = q({ page, size, status: params?.status, assetId: params?.assetId });
    const res = await api.get(`/api/hr/assets/maintenance?${qs}`);
    return normPage(res.data);
  },

  /** GET /api/hr/assets/maintenance/{id} — ASSET_MAINTENANCE */
  async getMaintenance(id: number): Promise<AssetMaintenance> {
    return (await api.get<AssetMaintenance>(`/api/hr/assets/maintenance/${id}`)).data;
  },

  /**
   * POST /api/hr/assets/maintenance — ASSET_MAINTENANCE
   * Body: MaintenanceCreateRequest
   */
  async createMaintenance(payload: MaintenanceCreateRequest): Promise<AssetMaintenance> {
    return (await api.post<AssetMaintenance>("/api/hr/assets/maintenance", payload)).data;
  },

  /**
   * PUT /api/hr/assets/maintenance/{id} — ASSET_MAINTENANCE
   * Body: MaintenanceUpdateRequest
   */
  async updateMaintenance(id: number, payload: MaintenanceUpdateRequest): Promise<AssetMaintenance> {
    return (await api.put<AssetMaintenance>(`/api/hr/assets/maintenance/${id}`, payload)).data;
  },

  // ── Disposals ─────────────────────────────────────────────────────────────

  /** GET /api/hr/assets/disposals?status&page&size — ASSET_DISPOSE */
  async listDisposals(
    page = 0,
    size = 10,
    params?: DisposalListParams,
  ): Promise<PageResponse<AssetDisposal>> {
    const qs = q({ page, size, status: params?.status });
    const res = await api.get(`/api/hr/assets/disposals?${qs}`);
    return normPage(res.data);
  },

  /** GET /api/hr/assets/disposals/inbox?page&size — ASSET_DISPOSE_APPROVE */
  async listDisposalInbox(page = 0, size = 10): Promise<PageResponse<AssetDisposal>> {
    const res = await api.get(`/api/hr/assets/disposals/inbox?${q({ page, size })}`);
    return normPage(res.data);
  },

  /** GET /api/hr/assets/disposals/{id} — ASSET_VIEW */
  async getDisposal(id: number): Promise<AssetDisposal> {
    return (await api.get<AssetDisposal>(`/api/hr/assets/disposals/${id}`)).data;
  },

  /** GET /api/hr/assets/disposals/asset/{assetId} — ASSET_VIEW */
  async getDisposalByAsset(assetId: number): Promise<AssetDisposal | null> {
    const res = await api.get<AssetDisposal>(`/api/hr/assets/disposals/asset/${assetId}`);
    return res.data ?? null;
  },

  /**
   * POST /api/hr/assets/disposals — ASSET_DISPOSE
   * Body: DisposalCreateRequest
   */
  async requestDisposal(payload: DisposalCreateRequest): Promise<AssetDisposal> {
    return (await api.post<AssetDisposal>("/api/hr/assets/disposals", payload)).data;
  },

  /**
   * POST /api/hr/assets/disposals/{id}/approve — ASSET_DISPOSE_APPROVE
   * Body: { approved, comments?, rejectionReason? }
   */
  async approveOrRejectDisposal(id: number, payload: DisposalApproveRequest): Promise<AssetDisposal> {
    return (await api.post<AssetDisposal>(`/api/hr/assets/disposals/${id}/approve`, payload)).data;
  },

  /**
   * POST /api/hr/assets/disposals/{id}/cancel — ASSET_DISPOSE
   * Cancel a PENDING disposal request
   */
  async cancelDisposal(id: number): Promise<AssetDisposal> {
    return (await api.post<AssetDisposal>(`/api/hr/assets/disposals/${id}/cancel`)).data;
  },

  /**
   * POST /api/hr/assets/disposals/{id}/complete — ASSET_MANAGE
   * Body: DisposalCompleteRequest
   */
  async completeDisposal(id: number, payload: DisposalCompleteRequest): Promise<AssetDisposal> {
    return (await api.post<AssetDisposal>(`/api/hr/assets/disposals/${id}/complete`, payload)).data;
  },

  // ── Import ────────────────────────────────────────────────────────────────

  /** GET /api/hr/assets/import/template — ASSET_IMPORT — returns xlsx blob */
  async downloadTemplate(): Promise<Blob> {
    const r = await api.get("/api/hr/assets/import/template", { responseType: "blob" });
    return r.data;
  },

  /**
   * POST /api/hr/assets/import  multipart/form-data { file: xlsx }
   * ASSET_IMPORT
   * Returns: { total, success, failed, results: ImportResultRow[] }
   * Each row includes rowData (original cells), warnings (non-fatal), error (fatal).
   */
  async importAssets(file: File): Promise<{
    total: number;
    success: number;
    failed: number;
    results: ImportResultRow[];
  }> {
    const form = new FormData();
    form.append("file", file);
    return (await api.post("/api/hr/assets/import", form)).data;
  },

  /**
   * POST /api/hr/assets/import/error-report
   * ASSET_IMPORT — Body: failed ImportResultRow[] from a previous import
   * Returns: xlsx blob with original rowData + Error Reason + Warnings columns
   */
  async downloadErrorReport(failedRows: ImportResultRow[]): Promise<Blob> {
    const r = await api.post("/api/hr/assets/import/error-report", failedRows, { responseType: "blob" });
    return r.data;
  },

  // ── Legacy report helpers (kept for AssetReportsPage depreciation tab) ─────

  /** GET /api/hr/assets/reports/flagged-for-disposal?page&size */
  async reportFlaggedForDisposal(page = 0, size = 20): Promise<PageResponse<AssetSummary>> {
    const res = await api.get(`/api/hr/assets/reports/flagged-for-disposal?${q({ page, size })}`);
    return normPage(res.data);
  },
};
